import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  AttachMoney as PricingIcon,
  Spa as PrestationIcon,
  DirectionsCar as VehicleIcon,
  People as UsersIcon,
  Engineering as TechnicianIcon,
  Build as MaintenanceIcon,
  BarChart as StatsIcon,
  Palette as BrandingIcon,
  Block as StopSellIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1920&q=80';

interface SettingSection {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
  badge?: string;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.auth.user);
  const isAdmin = currentUser?.role?.name === 'admin';

  const sections: SettingSection[] = [
    {
      title: 'Profil entreprise',
      description: 'Logo, coordonnées, informations légales et utilisateurs de votre compte.',
      icon: <BusinessIcon sx={{ fontSize: 32 }} />,
      path: '/profile',
      color: '#2c3e50',
    },
    {
      title: 'Template email',
      description: "Personnalisez l'apparence des emails envoyés à vos clients (en-tête, logo, pied de page).",
      icon: <EmailIcon sx={{ fontSize: 32 }} />,
      path: '/email-template',
      color: '#1565c0',
    },
    {
      title: 'Agences',
      description: 'Gérez vos points de départ et retour pour les réservations.',
      icon: <LocationOnIcon sx={{ fontSize: 32 }} />,
      path: '/reservations/agencies',
      color: '#6a1b9a',
    },
    {
      title: 'Tarification',
      description: 'Plans tarifaires, packs, grilles saisonnières et modèles de paliers.',
      icon: <PricingIcon sx={{ fontSize: 32 }} />,
      path: '/pricing/plans',
      color: '#e65100',
    },
    {
      title: 'Prestations',
      description: "Services optionnels proposés lors d'une réservation (assurances, équipements, etc.).",
      icon: <PrestationIcon sx={{ fontSize: 32 }} />,
      path: '/prestations',
      color: '#00695c',
    },
    {
      title: 'Catégories & types de véhicules',
      description: 'Configurez les types et catégories de votre flotte (économique, premium, utilitaire…).',
      icon: <VehicleIcon sx={{ fontSize: 32 }} />,
      path: '/vehicles',
      color: '#37474f',
    },
    {
      title: 'Utilisateurs',
      description: 'Gérez les comptes utilisateurs, rôles et accès à votre espace.',
      icon: <UsersIcon sx={{ fontSize: 32 }} />,
      path: '/profile',
      color: '#4527a0',
    },
    {
      title: 'Techniciens',
      description: 'Gérez vos techniciens/réparateurs externes et utilisez-les pour les assignations maintenance/intervention.',
      icon: <TechnicianIcon sx={{ fontSize: 32 }} />,
      path: '/settings/technicians',
      color: '#006064',
    },
    {
      title: 'Types de maintenance',
      description: 'Configurer les types de maintenance et interventions utilisés dans le suivi véhicule.',
      icon: <MaintenanceIcon sx={{ fontSize: 32 }} />,
      path: '/maintenance',
      color: '#c62828',
    },
    {
      title: 'Stop Sell',
      description: 'Bloquez les réservations sur une période pour une catégorie ou un véhicule, avec seuil de durée minimum.',
      icon: <StopSellIcon sx={{ fontSize: 32 }} />,
      path: '/stop-sell',
      color: '#b71c1c',
    },
    {
      title: 'Branding & apparence',
      description: "Personnalisez l'identité visuelle de l'interface (couleurs, images).",
      icon: <BrandingIcon sx={{ fontSize: 32 }} />,
      path: '/profile',
      color: '#ad1457',
    },
  ];

  const adminSections: SettingSection[] = isAdmin
    ? [
        {
          title: 'Statistiques globales',
          description: 'Vue administrateur sur tous les clients, réservations et usage.',
          icon: <StatsIcon sx={{ fontSize: 32 }} />,
          path: '/dashboard',
          color: '#1b5e20',
        },
      ]
    : [];

  const allSections = [...sections, ...adminSections];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 160, md: 200 },
          backgroundImage: `linear-gradient(135deg, rgba(44,62,80,0.93) 0%, rgba(52,73,94,0.88) 100%), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          mb: 4,
        }}
      >
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
          Paramètres
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Configuration et préférences de votre compte
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, pb: 6 }}>
        <Grid container spacing={3}>
          {allSections.map((section) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={section.title}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 3,
                  height: '100%',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    borderColor: section.color,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(section.path)}
                  sx={{ height: '100%', p: 0 }}
                >
                  <CardContent sx={{ p: 3, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: `${section.color}18`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: section.color,
                          flexShrink: 0,
                        }}
                      >
                        {section.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2c3e50' }}>
                            {section.title}
                          </Typography>
                          {section.badge && (
                            <Chip label={section.badge} size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {section.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Settings;
